/* bpl-remodel only: independent native add-on actions and actual cart totals.
   Turbo remains responsible for cart requests and drawer/header transport. */
(() => {
  'use strict';

  if (window.BPLRemodelProductAddons) {
    window.BPLRemodelProductAddons.init(document);
    return;
  }

  const selector = '.bpl-product--01.bpl-remodel [data-bpl-remodel-addons]';
  const states = new Map();
  let cart = null;
  let cartRequest = null;
  let cartHook = false;
  let cartRevision = 0;
  let refreshAfterRequest = false;
  const nativeRequests = new WeakMap();
  const pendingMutations = new Set();
  let readSequence = 0;
  let acceptedReadSequence = 0;

  function integer(value, minimum = 0) {
    const result = Number(value);
    return Number.isSafeInteger(result) && result >= minimum ? result : null;
  }

  function money(state, value) {
    const format = state.wrapper.dataset.moneyFormat;
    if (format && window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      try { return window.Shopify.formatMoney(value, format); } catch (_) { /* Use currency fallback. */ }
    }
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: state.currency }).format(value / 100);
  }

  function text(root, name, value) {
    root.querySelectorAll(`[data-bpl-${name}]`).forEach(node => {
      const next = String(value);
      if (node.textContent !== next) node.textContent = next;
    });
  }

  function visible(root, name, show) {
    root.querySelectorAll(`[data-bpl-${name}]`).forEach(node => { node.hidden = !show; });
  }

  function status(state, message) {
    text(state.panel, 'status', message);
    visible(state.panel, 'status', Boolean(message));
  }

  function cartURL(state) {
    try {
      const root = new URL(state.panel.dataset.root || '/', window.location.origin);
      if (root.origin !== window.location.origin || root.search || root.hash) return null;
      if (!root.pathname.endsWith('/')) root.pathname += '/';
      return new URL('cart.js', root).href;
    } catch (_) { return null; }
  }

  function validCart(value) {
    return value && integer(value.item_count) !== null && Array.isArray(value.items);
  }

  function acceptCart(value) {
    if (!validCart(value)) return false;
    cart = value;
    states.forEach(state => {
      state.cartFailed = false;
      if (state.awaitingCart) { state.awaitingCart = false; state.error = ''; }
      if (!pendingMutations.size && state.confirmation) {
        const confirmation = state.confirmation;
        if (confirmation.mainIncluded && confirmation.mainRevision === state.mainRevision) state.forceMain = false;
        confirmation.extras.forEach(entry => {
          if (entry.item.editVersion === entry.editVersion) entry.item.input.checked = false;
        });
        if (confirmation.version === state.selectionVersion) state.phase = 'cart';
        state.confirmation = null;
      }
      schedule(state);
      finishCheckout(state);
    });
    return true;
  }

  function readCart(state, fresh = false) {
    if (pendingMutations.size) {
      refreshAfterRequest = true;
      return Promise.resolve(false);
    }
    if (cartRequest) {
      if (fresh) refreshAfterRequest = true;
      return cartRequest;
    }
    const url = cartURL(state);
    if (!url) {
      state.cartFailed = true;
      state.checkoutIntent = null;
      schedule(state);
      return Promise.resolve(false);
    }
    const revision = cartRevision;
    const sequence = ++readSequence;
    refreshAfterRequest = false;
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 15000);
    cartRequest = window.fetch(url, { credentials: 'same-origin', cache: 'no-store', signal: controller.signal, headers: { Accept: 'application/json' } })
      .then(response => {
        if (!response.ok) throw new Error('Cart unavailable');
        return response.json();
      })
      .then(value => {
        if (controller.signal.aborted) throw new Error('Cart read timed out');
        if (revision !== cartRevision || sequence < acceptedReadSequence) return true;
        if (!acceptCart(value)) throw new Error('Cart unavailable');
        acceptedReadSequence = sequence;
        return true;
      })
      .catch(() => {
        if (revision !== cartRevision || sequence < acceptedReadSequence) return false;
        cart = null;
        states.forEach(item => { item.cartFailed = true; item.checkoutIntent = null; schedule(item); });
        return false;
      })
      .finally(() => {
        window.clearTimeout(timeout);
        cartRequest = null;
        if (refreshAfterRequest && states.size && !pendingMutations.size) {
          refreshAfterRequest = false;
          readCart(states.values().next().value);
        }
      });
    return cartRequest;
  }

  function cartPath(url) {
    try {
      const target = new URL(url, window.location.origin);
      return target.origin === window.location.origin && /\/cart(?:\/(?:add|change|update|clear))?\.js$/.test(target.pathname);
    } catch (_) { return false; }
  }

  function bindCartHook() {
    if (cartHook || !window.jQuery) return;
    cartHook = true;
    window.jQuery(document).on('ajaxSend.bplRemodelProductAddons', (_event, request, settings) => {
      if (!states.size || !cartPath(settings.url)) return;
      const action = new URL(settings.url, window.location.origin).pathname.match(/\/cart\/(add|change|update|clear)\.js$/);
      const mutation = Boolean(action);
      let attempt = null;
      let addon = null;
      if (mutation) {
        cartRevision += 1;
        pendingMutations.add(request);
        invalidateCart();
        if (action[1] === 'add') {
          const payload = requestItems(settings.data);
          if (payload) states.forEach(state => {
            if (!addon && state.addonAttempt && state.addonAttempt.payload === payload) {
              addon = state.addonAttempt;
              addon.request = request;
              state.addonAttempt = null;
            }
            if (!attempt && state.attempt && state.attempt.payload === payload) {
              attempt = { ...state.attempt, state, payload };
              state.inFlight = attempt;
              clearAttempt(state);
            }
          });
        }
      }
      nativeRequests.set(request, { revision: cartRevision, sequence: mutation ? 0 : ++readSequence, mutation, attempt, addon });
    });
    window.jQuery(document).on('ajaxComplete.bplRemodelProductAddons', (_event, request, settings) => {
      if (!states.size || !cartPath(settings.url)) return;
      const meta = nativeRequests.get(request);
      nativeRequests.delete(request);
      const mutation = meta ? meta.mutation : /\/cart\/(add|change|update|clear)\.js$/.test(new URL(settings.url, window.location.origin).pathname);
      const succeeded = request.status >= 200 && request.status < 300 && !(request.responseJSON && Number(request.responseJSON.status) >= 400);
      if (mutation) {
        pendingMutations.delete(request);
        cartRevision += 1;
        invalidateCart();
        if (meta && meta.attempt && states.has(meta.attempt.state.panel)) {
          const state = meta.attempt.state;
          state.inFlight = null;
          const added = succeeded && validAddResult(request.responseJSON, meta.attempt.payload);
          if (added) state.confirmation = meta.attempt;
          if (meta.attempt.version === state.selectionVersion) {
            state.addFailed = !added;
            state.addedVersion = added ? meta.attempt.version : null;
            // Even Shopify's 422 inventory response can add part of a request.
            // Never retry a failed/partial request as another full add.
            state.uncertainVersion = !added ? meta.attempt.version : null;
            if (!added) state.checkoutIntent = null;
            state.checkoutMessage = state.uncertainVersion !== null ? 'Please review your cart before trying again.' : '';
          }
          if (!added) {
            state.addFailed = true;
            state.uncertainVersion = state.selectionVersion;
            state.checkoutIntent = null;
            state.checkoutMessage = 'Please review your cart before trying again.';
          }
        }
        if (meta && meta.addon) finishAddon(meta.addon.state, meta.addon, request);
        // A read started before this mutation finished cannot confirm the result.
        // Wait for all observed mutations before taking the final cart snapshot.
        readCart(states.values().next().value, true);
      } else if (meta && meta.revision === cartRevision && meta.sequence >= acceptedReadSequence && !pendingMutations.size && succeeded && validCart(request.responseJSON)) {
        acceptCart(request.responseJSON);
        acceptedReadSequence = meta.sequence;
      } else if (meta && meta.revision === cartRevision && meta.sequence >= acceptedReadSequence && !pendingMutations.size && (!succeeded || !validCart(request.responseJSON))) {
        cart = null;
        states.forEach(state => { state.cartFailed = true; state.checkoutIntent = null; schedule(state); });
      }
      states.forEach(schedule);
    });
  }

  function invalidateCart() {
    cart = null;
    states.forEach(state => { state.cartFailed = false; schedule(state); });
  }

  function requestItems(data) {
    try {
      if (typeof data === 'string' && data.trim().startsWith('{')) data = JSON.parse(data);
      if (data && Array.isArray(data.items)) return itemFingerprint(data.items);
      const entries = typeof data === 'string' ? Array.from(new URLSearchParams(data)) : data && typeof data.entries === 'function' ? Array.from(data.entries()) : data && typeof data === 'object' ? Object.entries(data) : [];
      const groups = new Map();
      const single = {};
      for (const [name, value] of entries) {
        if (name === 'id' || name === 'quantity') {
          if (Object.prototype.hasOwnProperty.call(single, name)) return null;
          single[name] = value;
        }
        const match = /^items\[(\d+)\]\[(id|quantity)\]$/.exec(name);
        if (!match) continue;
        const index = Number(match[1]);
        const group = groups.get(index) || {};
        if (Object.prototype.hasOwnProperty.call(group, match[2])) return null;
        group[match[2]] = value;
        groups.set(index, group);
      }
      if (groups.size && Object.keys(single).length) return null;
      return itemFingerprint(groups.size ? Array.from(groups).sort((a, b) => a[0] - b[0]).map(entry => entry[1]) : [single]);
    } catch (_) { return null; }
  }

  function itemFingerprint(items) {
    if (!items.length) return null;
    const values = [];
    for (const item of items) {
      const id = String(item.id || '');
      const quantity = integer(item.quantity === undefined ? 1 : item.quantity, 1);
      if (!/^\d+$/.test(id) || quantity === null) return null;
      values.push([id, quantity]);
    }
    return JSON.stringify(values);
  }

  function validAddResult(value, payload) {
    if (!value || value.item_count !== undefined) return false;
    const items = Array.isArray(value.items) ? value.items : value.id ? [value] : [];
    return JSON.parse(payload).every(([id, quantity]) => items.some(item => String(item.id) === id && integer(item.quantity, 1) !== null && Number(item.quantity) >= quantity));
  }

  function clearAttempt(state) {
    state.attempt = null;
    if (state.attemptTimer) window.clearTimeout(state.attemptTimer);
    state.attemptTimer = 0;
  }

  function armAttempt(state, event) {
    clearAttempt(state);
    if (event.defaultPrevented || event.prevented || state.button.disabled || state.button.classList.contains('disabled')) return;
    const plan = state.prepared ? state.prepared.plan : pendingPlan(state);
    if (!plan || unsupportedPlan(state)) return;
    state.attempt = { version: state.selectionVersion, payload: itemFingerprint(plan.items), mainIncluded: plan.mainIncluded,
      mainRevision: state.mainRevision, extras: plan.extras.map(item => ({ item, editVersion: item.editVersion })) };
    // Turbo serializes in the native event turn. Unmatched/later requests cannot
    // be mistaken for this form's successful add.
    state.attemptTimer = window.setTimeout(() => {
      clearAttempt(state);
      if (!state.inFlight) state.checkoutIntent = null;
      schedule(state);
    }, 0);
  }

  function editSelection(state, mainChanged = false) {
    clearAttempt(state);
    const uncertain = state.uncertainVersion !== null;
    if (mainChanged) { state.mainRevision += 1; state.forceMain = true; }
    state.selectionVersion += 1;
    state.phase = 'pending';
    state.addFailed = uncertain;
    state.error = '';
    state.checkoutIntent = null;
    state.addedVersion = null;
    state.uncertainVersion = uncertain ? state.selectionVersion : null;
    state.checkoutMessage = uncertain ? 'Please review your cart before trying again.' : '';
  }

  function destination(state, path) {
    const url = cartURL(state);
    return url ? new URL(path, url).href : null;
  }

  function checkoutBusy(state) {
    return Boolean(state.inFlight || state.attempt || state.addonTransport || pendingMutations.size || state.navigating || (state.checkoutIntent && state.addedVersion === state.selectionVersion && cartRequest));
  }

  function goToCheckout(state, path = 'checkout') {
    const url = destination(state, path);
    if (!url || state.navigating || !states.has(state.panel) || !state.panel.isConnected) return;
    state.navigating = true;
    state.checkoutIntent = null;
    syncCheckout(state);
    window.location.assign(url);
  }

  function finishCheckout(state) {
    const intent = state.checkoutIntent;
    if (!intent || intent.version !== state.selectionVersion || state.addedVersion !== state.selectionVersion || state.inFlight || state.attempt || pendingMutations.size || state.navigating || state.cartFailed) return;
    const amounts = cartAmounts(state);
    if (!amounts || amounts.count < 1) {
      state.checkoutIntent = null;
      return;
    }
    goToCheckout(state);
  }

  function syncCheckout(state) {
    if (!state.checkoutButton) return;
    const unsupported = unsupportedPlan(state);
    const busy = checkoutBusy(state);
    const added = state.phase === 'cart' || state.addedVersion === state.selectionVersion;
    const uncertain = state.uncertainVersion === state.selectionVersion;
    const amounts = cartAmounts(state);
    const plan = pendingPlan(state);
    state.checkoutButton.hidden = unsupported;
    state.checkoutButton.disabled = Boolean(unsupported || !destination(state, 'checkout') || busy || (!added && !uncertain && (!plan || state.button.disabled || state.button.classList.contains('disabled'))) || (added && amounts && amounts.count === 0));
    const label = state.navigating ? 'Opening…' : busy ? (added && !state.inFlight ? 'Checking cart…' : 'Adding…') : uncertain ? 'Review cart' : added ? 'Checkout' : 'Add & checkout';
    if (state.checkoutButton.textContent !== label) state.checkoutButton.textContent = label;
    state.checkoutButton.setAttribute('aria-busy', busy ? 'true' : 'false');
    const failedRead = added && (state.cartFailed || (cart && !amounts));
    const message = state.checkoutMessage || (failedRead ? 'Your items were added. Click Checkout to check your cart again.' : '');
    state.checkoutStatus.textContent = message;
    state.checkoutStatus.hidden = !message;
  }

  function checkoutClick(event, state) {
    event.preventDefault();
    event.stopImmediatePropagation();
    observeMainChange(state, false);
    if (checkoutBusy(state) || unsupportedPlan(state)) return;
    if (state.uncertainVersion === state.selectionVersion) return goToCheckout(state, 'cart');
    if (state.phase === 'cart' || state.addedVersion === state.selectionVersion) {
      const amounts = cartAmounts(state);
      if (amounts && amounts.count > 0 && !state.cartFailed) return goToCheckout(state);
      state.checkoutIntent = { version: state.selectionVersion };
      readCart(state, true);
      syncCheckout(state);
      return;
    }
    if (!mainSelection(state) || state.button.disabled || state.button.classList.contains('disabled') || !state.form.reportValidity()) return;
    state.checkoutMessage = '';
    state.checkoutIntent = { version: state.selectionVersion };
    // Use Turbo's existing synchronous serialization and request exactly once.
    // Do not disable the native button or controls before its click handler runs.
    state.button.click();
    if (!state.attempt && !state.inFlight) state.checkoutIntent = null;
    syncCheckout(state);
  }

  function attachCheckout(state) {
    const host = state.button.closest('.purchase-details__buttons');
    if (!host) return;
    const button = document.createElement('button');
    button.type = 'button';
    button.classList.add('global-button');
    button.classList.add('bpl-checkout-button');
    button.setAttribute('data-bpl-checkout', '');
    button.textContent = 'Add & checkout';
    const note = document.createElement('p');
    note.classList.add('bpl-checkout-status');
    note.setAttribute('role', 'status');
    note.setAttribute('aria-live', 'polite');
    note.hidden = true;
    state.button.insertAdjacentElement('afterend', button);
    button.insertAdjacentElement('afterend', note);
    host.classList.add('bpl-checkout-buttons');
    const layout = host.closest('.purchase-details');
    if (layout) {
      layout.classList.add('bpl-checkout-layout');
      layout.classList.toggle('bpl-checkout-with-quantity', Boolean(layout.querySelector('.purchase-details__quantity')));
    }
    state.checkoutButton = button;
    state.checkoutStatus = note;
    state.checkoutHost = host;
    state.checkoutLayout = layout;
  }

  function mainSelection(state) {
    const id = state.master.value;
    const variant = state.product.variants.find(item => String(item.id) === id);
    const quantityInput = state.form.querySelector('[name="quantity"]');
    const quantity = quantityInput ? integer(quantityInput.value, 1) : 1;
    if (!variant || !variant.available || integer(variant.price) === null || quantity === null) return null;
    if (quantityInput) {
      const min = quantityInput.getAttribute('min');
      const max = quantityInput.getAttribute('max');
      if ((min !== null && quantity < Number(min)) || (max !== null && quantity > Number(max))) return null;
    }
    return { variant, quantity, price: Number(variant.price) };
  }

  function selected(state) {
    // Add-ons are immediate, independent actions; they never join the main form.
    return [];
  }

  function mainProperties(state) {
    const properties = [];
    for (const [name, value] of new FormData(state.form).entries()) {
      if (!name.startsWith('properties[')) continue;
      const match = /^properties\[([^\]]+)\]$/.exec(name);
      if (!match || typeof value !== 'string') return null;
      if (value !== '') properties.push([match[1], value]);
    }
    return properties.sort((a, b) => a[0].localeCompare(b[0]) || a[1].localeCompare(b[1]));
  }

  function mainIdentity(state) {
    if (state.prepared) return state.prepared.plan.identity;
    const quantity = state.form.querySelector('[name="quantity"]');
    const plans = Array.from(state.form.querySelectorAll('[name="selling_plan"]')).filter(control => !control.disabled).map(control => control.value);
    return JSON.stringify([String(state.master.value), quantity ? String(quantity.value) : '1', mainProperties(state), plans]);
  }

  function observeMainChange(state, defer = true) {
    const identity = mainIdentity(state);
    if (identity !== state.mainIdentity) {
      state.mainIdentity = identity;
      editSelection(state, true);
      schedule(state);
    }
    // Variant/quantity widgets may update the native controls later in this event turn.
    if (defer && !state.mainEditTimer) state.mainEditTimer = window.setTimeout(() => {
      state.mainEditTimer = 0;
      if (states.has(state.panel)) observeMainChange(state, false);
    }, 0);
  }

  function pendingPlan(state) {
    const main = mainSelection(state);
    if (!main) return null;
    const extras = selected(state);
    if (extras.length && (!cart || state.cartFailed || pendingMutations.size || !cartAmounts(state))) return null;
    const properties = mainProperties(state);
    const existingMain = properties !== null && cart && cart.items.some(item => {
      if (String(item.variant_id || item.id) !== String(main.variant.id) || integer(item.quantity, 1) === null || item.selling_plan_allocation) return false;
      const entries = Object.entries(item.properties || {}).filter(([, value]) => value !== '' && value !== null)
        .map(([name, value]) => [name, String(value)]).sort((a, b) => a[0].localeCompare(b[0]) || a[1].localeCompare(b[1]));
      return JSON.stringify(entries) === JSON.stringify(properties);
    });
    const mainIncluded = !extras.length || state.forceMain || !existingMain;
    const items = (mainIncluded ? [{ id: main.variant.id, quantity: main.quantity }] : [])
      .concat(extras.map(item => ({ id: item.id, quantity: 1 })));
    return { main, extras, mainIncluded, existingMain: Boolean(existingMain), items, properties, identity: mainIdentity(state),
      count: (mainIncluded ? main.quantity : 0) + extras.length,
      original: (mainIncluded ? main.price * main.quantity : 0) + extras.reduce((sum, item) => sum + item.price, 0) };
  }

  function configuredRate(state, count) {
    return Math.max(count >= state.low.count ? state.low.rate : 0, count >= state.high.count ? state.high.rate : 0);
  }

  // These active, non-combinable native automatic ORDER rules were verified in
  // Shopify Admin for this test on 2026-09-30. Product titles do not limit eligibility.
  const verifiedRules = [
    { count: 2, rate: 10, title: '10% Off Applied — Add 1 More Butt Plug for 15% Off' },
    { count: 3, rate: 15, title: '15% Off Applied on 3+ Butt Plugs' }
  ];

  function verifiedRate(count) {
    return verifiedRules.reduce((rate, rule) => count >= rule.count ? Math.max(rate, rule.rate) : rate, 0);
  }

  function verifiedSettings(state) {
    return state.low.count === 2 && state.low.rate === 10 && state.high.count === 3 && state.high.rate === 15;
  }

  function supportedDiscounts(value, rate) {
    const applications = Array.isArray(value.cart_level_discount_applications) ? value.cart_level_discount_applications : [];
    if (applications.length > 1) return false;
    if (!applications.length && (Number(value.total_discount) > 0 || (value.item_count > 0 && integer(value.total_discount) === null))) return false;
    for (const application of applications) {
      const percent = Number(application.value);
      // Native automatic order discounts can report a blank type. Require the
      // observed allocation metadata as well as the percentage/target checks.
      const knownType = application.type === 'automatic' || (application.type === '' && application.allocation_method === 'across' && integer(application.total_allocated_amount) !== null);
      const rule = verifiedRules.find(item => item.rate === percent && item.title === String(application.title || '').trim());
      if (!rule || percent !== verifiedRate(value.item_count)) return false;
      if (!knownType || application.value_type !== 'percentage' || application.target_type !== 'line_item' || application.target_selection !== 'all' || application.value === null || application.value === '' || !Number.isFinite(percent) || percent < 0 || percent > rate) return false;
      if (application.allocation_method && application.allocation_method !== 'across') return false;
      const combinations = application.combines_with || application.combinesWith;
      if (combinations && Object.values(combinations).some(Boolean)) return false;
    }
    if (value.items.some(item => item.gift_card || item.selling_plan_allocation || Number(item.line_level_total_discount) > 0 || (Array.isArray(item.line_level_discount_allocations) && item.line_level_discount_allocations.length > 0))) return false;
    const knownTitles = new Set(applications.map(application => String(application.title || '').trim().toLowerCase()));
    if (value.items.some(item => Array.isArray(item.discounts) && item.discounts.some(discount => Number(discount.amount) > 0 && !knownTitles.has(String(discount.title || '').trim().toLowerCase())))) return false;
    if (Array.isArray(value.discount_codes) && value.discount_codes.some(code => code.applicable !== false && !knownTitles.has(String(code.code || '').trim().toLowerCase()))) return false;
    return true;
  }

  function confirmedDiscount(state, count) {
    return Boolean(cart && verifiedSettings(state) && (!cart.currency || cart.currency === state.currency) && supportedDiscounts(cart, configuredRate(state, count))
      && (verifiedRate(cart.item_count) === 0 || cart.original_total_price === 0 || (Array.isArray(cart.cart_level_discount_applications) && cart.cart_level_discount_applications.length === 1)));
  }

  function tier(state, count) {
    return confirmedDiscount(state, count) ? configuredRate(state, count) : 0;
  }

  function cartAmounts(state) {
    if (!cart || cart.currency !== state.currency || typeof cart.item_count !== 'number' || integer(cart.item_count) === null) return null;
    const fields = ['original_total_price', 'items_subtotal_price', 'total_discount', 'total_price'];
    if (fields.some(name => typeof cart[name] !== 'number' || integer(cart[name]) === null)) return null;
    if (cart.original_total_price - cart.total_discount !== cart.total_price || cart.items_subtotal_price > cart.original_total_price || cart.items_subtotal_price < cart.total_price) return null;
    let count = 0;
    let original = 0;
    const lines = [];
    for (const item of cart.items) {
      if (typeof item.quantity !== 'number' || integer(item.quantity, 1) === null) return null;
      if (typeof item.original_line_price !== 'number' || integer(item.original_line_price) === null) return null;
      count += item.quantity;
      original += item.original_line_price;
      if (!Number.isSafeInteger(count) || !Number.isSafeInteger(original)) return null;
      lines.push({ title: item.title, quantity: item.quantity, original: item.original_line_price });
    }
    if (count !== cart.item_count || original !== cart.original_total_price || (count === 0 && cart.original_total_price !== 0)) return null;
    return { count, original: cart.original_total_price, saving: cart.total_discount, total: cart.total_price, lines };
  }

  function unsupportedPlan(state) {
    return Array.from(state.form.querySelectorAll('[name="selling_plan"]')).some(control => !control.disabled && control.value);
  }

  function syncOffer(state, main, extras, known) {
    // Retain the last confirmed offer only while its cart is being refreshed.
    const retainPendingOffer = document.body.classList.contains('product-bpl-remodel')
      && main && verifiedSettings(state) && !state.error && !state.cartFailed && !state.addFailed
      && !known && !cart && (pendingMutations.size || cartRequest);
    const offer = retainPendingOffer && state.panel.querySelector('[data-bpl-offer]');
    if (offer && !offer.hidden) return;

    // Only advertise thresholds that improve on every earlier eligible rate.
    // This follows the existing quote when merchant rates are zero, equal, or reversed.
    const tiers = [];
    [state.low, state.high].sort((a, b) => a.count - b.count || b.rate - a.rate).forEach(item => {
      if (item.rate > (tiers.length ? tiers[tiers.length - 1].rate : 0)) tiers.push(item);
    });
    const show = Boolean(main && known && !state.error && !state.cartFailed && !state.addFailed && tiers.length);
    visible(state.panel, 'offer', show);
    visible(state.panel, 'offer-tiers', show);
    ['low', 'high'].forEach((name, index) => {
      const item = show ? tiers[index] : null;
      const next = item ? tiers[index + 1] : null;
      const quantity = item ? next ? (next.count === item.count + 1 ? String(item.count) : `${item.count}–${next.count - 1}`) : `${item.count}+` : '';
      text(state.panel, `tier-${name}-count`, item ? `${quantity} items` : '');
      text(state.panel, `tier-${name}-rate`, item ? `${item.rate}% off your entire order` : '');
      visible(state.panel, `offer-tier-${name}`, Boolean(item));
    });
  }

  function itemTitle(value, fallback) {
    return typeof value === 'string' && value.trim() ? value.trim().replace(/\s+/g, ' ') : fallback;
  }

  function addonTitle(item) {
    const row = item.input.closest('[data-bpl-addon-row]');
    const name = row && row.querySelector('.bpl-addons__name');
    if (!name) return 'Selected extra';
    // A native block variant span may directly follow the product text node.
    // Preserve both complete pieces with a readable boundary between them.
    const children = Array.from(name.childNodes || []).filter(node => node.nodeType === 1 || node.nodeType === 3);
    return itemTitle(children.length ? children.map(node => node.textContent).join(' ') : name.textContent, 'Selected extra');
  }

  function primaryLabel() { return 'Add to Cart'; }

  function discountSummary(state, actual) {
    if (!actual) return { label: 'Discount', value: '—' };
    if (actual.saving === 0) return { label: 'No discount applied', value: '—' };
    // Report only a single applied, order-wide percentage from Shopify metadata.
    // Never derive it from item count, a promotion tier, or rounded money amounts.
    const applications = Array.isArray(cart.cart_level_discount_applications) ? cart.cart_level_discount_applications : [];
    const application = applications.length === 1 ? applications[0] : null;
    const rate = application ? Number(application.value) : NaN;
    const allocated = application ? integer(application.total_allocated_amount) : null;
    const lineDiscount = cart.items.some(item => Number(item.line_level_total_discount) > 0
      || (Array.isArray(item.line_level_discount_allocations) && item.line_level_discount_allocations.some(discount => Number(discount.amount) > 0)));
    const singlePercentage = application && application.value_type === 'percentage' && application.target_type === 'line_item'
      && application.target_selection === 'all' && Number.isFinite(rate) && rate > 0 && rate <= 100
      && allocated === actual.saving && allocated > 0 && !lineDiscount;
    return { label: singlePercentage ? `${rate}% discount applied` : 'Discount applied', value: `${money(state, actual.saving)} saved` };
  }

  function syncSummary(state) {
    const updating = Boolean(state.inFlight || state.attempt || state.addonTransport || pendingMutations.size || state.confirmation);
    const actual = !state.cartFailed && !updating ? cartAmounts(state) : null;
    visible(state.panel, 'summary', true);
    visible(state.panel, 'cart-group', false);
    visible(state.panel, 'adding-group', false);
    ['subtotal-row', 'discount-row', 'total-row'].forEach(name => visible(state.panel, name, true));
    const subtotalLabel = state.panel.querySelector('[data-bpl-subtotal-row] > span:first-child');
    if (subtotalLabel && subtotalLabel.textContent !== 'Cart subtotal') subtotalLabel.textContent = 'Cart subtotal';
    text(state.panel, 'subtotal', actual ? money(state, actual.total) : '—');
    const discount = discountSummary(state, actual);
    text(state.panel, 'discount', discount.label);
    text(state.panel, 'saving', discount.value);
    text(state.panel, 'total-label', 'USA shipping');
    text(state.panel, 'total', 'FREE');
    const note = actual ? '' : state.cartFailed ? 'Cart details are unavailable. Please refresh the page.' : updating ? 'Updating your cart…' : 'Loading your cart…';
    text(state.panel, 'pricing-note', note);
    visible(state.panel, 'pricing-note', Boolean(note));
  }

  function sync(state) {
    if (!state.panel.isConnected) return;
    // A fresh page can already contain this exact product in the cart. Display the
    // actual order until the customer changes the main selection or chooses extras.
    // The native primary button still explicitly adds another main product.
    if (cart && !state.cartFailed && !pendingMutations.size && !state.inFlight && !state.attempt && !selected(state).length && !state.forceMain && cartAmounts(state)) {
      const current = pendingPlan(state);
      if (current && current.existingMain) {
        state.phase = 'cart';
        state.addedVersion = state.selectionVersion;
        state.cartAdopted = true;
      } else if (state.cartAdopted) {
        state.phase = 'pending';
        state.addedVersion = null;
        state.cartAdopted = false;
      }
    }
    syncCheckout(state);
    syncAddonControls(state);
    if (unsupportedPlan(state)) {
      state.panel.hidden = true;
      state.addons.forEach(item => { item.input.checked = false; });
      state.form.classList.remove('bpl-addons-has-selection');
      state.form.classList.remove('bpl-addons-extras-only');
      state.root.classList.remove('bpl-addons-active');
      if (!state.button.disabled && state.buttonText.textContent === state.lastLabel) state.buttonText.textContent = state.originalLabel;
      state.lastLabel = state.originalLabel;
      return;
    }
    if (state.checkoutOnly) {
      state.form.classList.remove('bpl-addons-extras-only');
      state.panel.hidden = !state.error;
      return;
    }
    state.panel.hidden = false;
    const extras = selected(state);
    state.form.classList.toggle('bpl-addons-has-selection', extras.length > 0);
    state.root.classList.add('bpl-addons-active');
    state.addons.forEach(item => {
      const row = item.input.closest('[data-bpl-addon-row]');
      if (row) row.classList.remove('bpl-addon-is-selected');
    });
    const main = mainSelection(state);
    const plan = pendingPlan(state);
    state.form.classList.toggle('bpl-addons-extras-only', Boolean(plan && extras.length && !plan.mainIncluded));
    const known = Boolean(cartAmounts(state) && confirmedDiscount(state, cart.item_count));
    syncOffer(state, main, extras, known);
    syncSummary(state);
    if (!main) {
      if (extras.length) status(state, 'Choose an available product option and quantity.');
      return;
    }
    if (!state.error) status(state, '');
    if (!state.button.disabled && !state.button.classList.contains('disabled')) {
      const label = primaryLabel(state, plan, extras);
      if (state.buttonText.textContent !== label) state.buttonText.textContent = label;
      state.lastLabel = label;
    }
  }

  function schedule(state) {
    if (!states.has(state.panel) || state.frame) return;
    state.frame = window.requestAnimationFrame(() => { state.frame = 0; sync(state); });
  }

  function stop(event, state, message) {
    event.preventDefault();
    event.stopImmediatePropagation();
    state.error = message;
    status(state, message);
    sync(state);
  }

  function cleanupPayload(state) {
    if (!state.prepared) return;
    state.prepared.controls.forEach(entry => { entry.control.disabled = entry.disabled; });
    state.prepared.container.remove();
    state.prepared = null;
    if (state.cleanupTimer) window.clearTimeout(state.cleanupTimer);
    state.cleanupTimer = 0;
  }

  function syncAddonControls(state) {
    state.addons.forEach(item => {
      const busy = item.pending || Boolean(pendingMutations.size) || Boolean(state.addonTransport);
      const added = item.addedUntil > Date.now();
      item.input.disabled = item.originalDisabled || item.input.dataset.available !== 'true' || busy || added;
      item.input.setAttribute('aria-busy', item.pending ? 'true' : 'false');
      const label = item.pending ? 'Adding…' : added ? 'Added' : item.uncertain ? 'Review cart' : '+ Add';
      if (item.input.textContent !== label) item.input.textContent = label;
    });
  }

  function cleanupAddonTransport(state, transport) {
    if (!transport) return;
    if (transport.armTimer) window.clearTimeout(transport.armTimer);
    if (transport.watchdog) window.clearTimeout(transport.watchdog);
    transport.host.remove();
    if (state.addonAttempt === transport) state.addonAttempt = null;
    if (state.addonTransport === transport) state.addonTransport = null;
  }

  function finishAddon(state, transport, request, forcedUncertain = false) {
    if (!transport || transport.done) return;
    transport.done = true;
    cleanupAddonTransport(state, transport);
    const item = transport.item;
    item.pending = false;
    let response = request && request.responseJSON;
    if (!response && request && request.responseText) {
      try { response = JSON.parse(request.responseText); } catch (_) { /* Unknown outcome: never retry automatically. */ }
    }
    const statusCode = request ? request.status : 0;
    const added = !forcedUncertain && statusCode >= 200 && statusCode < 300 && validAddResult(response, transport.payload);
    item.uncertain = !added && (forcedUncertain || (request && (!statusCode || statusCode >= 500 || (statusCode >= 200 && statusCode < 300))));
    if (added) {
      item.addedUntil = Date.now() + 1500;
      if (item.feedbackTimer) window.clearTimeout(item.feedbackTimer);
      item.feedbackTimer = window.setTimeout(() => { item.feedbackTimer = 0; schedule(state); }, 1550);
    } else {
      const message = response && typeof response.description === 'string' ? response.description : '';
      state.error = item.uncertain ? 'We could not confirm this add. Please review your cart before trying again.' : message || 'This item could not be added. Please try again.';
      if (states.has(state.panel)) status(state, state.error);
    }
    if (states.has(state.panel)) syncAddonControls(state);
    schedule(state);
  }

  function addonClick(event, state, item) {
    event.preventDefault();
    event.stopImmediatePropagation();
    if (item.pending || item.input.disabled || state.addonTransport || pendingMutations.size || state.navigating) return;
    if (item.uncertain) return goToCheckout(state, 'cart');
    // This action is independent of the main variant, availability and quantity.
    item.pending = true;
    item.addedUntil = 0;
    state.error = '';
    status(state, '');
    const document = state.form.ownerDocument;
    const host = document.createElement('div');
    host.hidden = true;
    host.setAttribute('data-bpl-addon-transport', '');
    const form = document.createElement('form');
    form.method = 'post';
    form.action = destination(state, 'cart/add') || '/cart/add';
    [['id', item.id], ['quantity', '1']].forEach(([name, value]) => {
      const input = document.createElement('input');
      input.type = 'hidden'; input.name = name; input.value = value;
      form.appendChild(input);
    });
    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'add_to_cart ajax-submit';
    const label = document.createElement('span');
    label.className = 'text'; label.textContent = 'Add to Cart';
    trigger.appendChild(label);
    form.appendChild(trigger);
    host.appendChild(form);
    document.body.appendChild(host);
    const transport = { state, item, host, payload: itemFingerprint([{ id: item.id, quantity: 1 }]), request: null, done: false, armTimer: 0, watchdog: 0 };
    state.addonAttempt = transport;
    state.addonTransport = transport;
    syncAddonControls(state);
    schedule(state);
    // The native delegated handler owns POST, drawer/header refresh and its animations.
    // The hidden host also contains any native warning, so cleanup cannot erase unrelated errors.
    transport.watchdog = window.setTimeout(() => {
      if (transport.done) return;
      const request = transport.request;
      try { if (request) request.abort(); } catch (_) { /* Native error callbacks can reject a malformed response. */ }
      if (transport.done) return;
      if (request) { pendingMutations.delete(request); nativeRequests.delete(request); }
      cartRevision += 1;
      invalidateCart();
      finishAddon(state, transport, request, true);
      if (states.size) readCart(states.values().next().value, true);
    }, 20000);
    transport.armTimer = window.setTimeout(() => {
      if (!transport.request && !transport.done) finishAddon(state, transport, null);
    }, 0);
    try { trigger.click(); } catch (_) { /* The watchdog handles an unknown native request outcome. */ }
  }

  function attach(panel) {
    if (states.has(panel)) return schedule(states.get(panel));
    const form = panel.closest('form');
    const wrapper = form && form.closest('[data-product-form]');
    const button = form && form.querySelector('.add_to_cart.ajax-submit');
    const master = form && form.querySelector('select[name="id"]');
    if (!form || !wrapper || !button || !master || !window.jQuery || !window.fetch || !window.PXUTheme || !window.PXUTheme.theme_settings || window.PXUTheme.theme_settings.cart_action !== 'ajax' || document.body.classList.contains('cart')) return;
    let product;
    try { product = JSON.parse(wrapper.dataset.product); } catch (_) { return; }
    if (!Array.isArray(product.variants) || product.requires_selling_plan || product.gift_card || (Array.isArray(product.selling_plan_groups) && product.selling_plan_groups.length > 0)) return;
    const buttonText = button.querySelector('span.text');
    if (!buttonText) return;
    const mainIds = new Set(product.variants.map(item => String(item.id)));
    const seen = new Set();
    const inputs = Array.from(panel.querySelectorAll('button[data-bpl-addon]'));
    const addons = [];
    for (const input of inputs) {
      const id = input.dataset.id;
      const price = integer(input.dataset.price);
      if (!/^\d+$/.test(id || '') || price === null || seen.has(id) || mainIds.has(id)) return;
      seen.add(id);
      addons.push({ input, id, price, originalDisabled: input.disabled, editVersion: 0, pending: false, uncertain: false, addedUntil: 0, feedbackTimer: 0 });
    }
    const mainPricingOnly = panel.getAttribute('data-bpl-checkout-only') !== null;
    const checkoutOnly = false;
    if (!addons.length && !mainPricingOnly) return;
    if (mainPricingOnly && !panel.querySelector('[data-bpl-summary]')) {
      panel.insertAdjacentHTML('afterbegin', "<div class=\"bpl-addons__summary\" data-bpl-summary aria-live=\"polite\" aria-atomic=\"true\"><div class=\"bpl-addons__cart-group\" data-bpl-cart-group hidden><span data-bpl-cart-heading></span><p data-bpl-cart-empty hidden></p><p data-bpl-cart-note></p><div data-bpl-cart-lines></div></div><div class=\"bpl-addons__order-group\" data-bpl-adding-group hidden><div data-bpl-adding-lines></div></div><div class=\"bpl-addons__order-totals\"><div class=\"bpl-addons__summary-line\" data-bpl-subtotal-row hidden><span>Subtotal</span><span class=\"bpl-addons__money\" data-bpl-subtotal></span></div><div class=\"bpl-addons__summary-line bpl-addons__saving\" data-bpl-discount-row hidden><span data-bpl-discount></span><span class=\"bpl-addons__money\" data-bpl-saving></span></div><p class=\"bpl-addons__pricing-note\" data-bpl-pricing-note hidden></p><div class=\"bpl-addons__summary-line bpl-addons__total\" data-bpl-total-row hidden><span data-bpl-total-label></span><span class=\"bpl-addons__money\" data-bpl-total></span></div></div></div>");
      panel.classList.remove('bpl-addons--checkout-only');
    }
    const low = { count: integer(panel.getAttribute('data-tier-2-count') || 2, 1), rate: integer(panel.getAttribute('data-tier-2-rate') || 10) };
    const high = { count: integer(panel.getAttribute('data-tier-3-count') || 3, 1), rate: integer(panel.getAttribute('data-tier-3-rate') || 15) };
    if (low.count === null || high.count === null || low.rate === null || high.rate === null || low.rate > 100 || high.rate > 100) return;
    const currency = panel.dataset.currency || wrapper.dataset.shopCurrency || 'USD';
    if (!/^[A-Z]{3}$/.test(currency)) return;
    const root = panel.closest('.bpl-product--01');
    const state = { panel, root, form, wrapper, button, buttonText, master, product, addons, low, high, currency, frame: 0, prepared: null, cleanupTimer: 0, error: '', cartFailed: false, phase: 'pending', selectionVersion: 0, attempt: null, attemptTimer: 0, confirmation: null, addFailed: false, originalLabel: button.getAttribute('data-label') || buttonText.textContent.trim(), lastLabel: '' };
    Object.assign(state, { checkoutOnly, inFlight: null, checkoutIntent: null, addedVersion: null, uncertainVersion: null, navigating: false, checkoutMessage: '', forceMain: false, mainRevision: 0, awaitingCart: false, cartAdopted: false, addonAttempt: null, addonTransport: null });
    state.mainIdentity = mainIdentity(state);
    state.mainEditTimer = 0;
    // Liquid may serialize the native label as JSON; prefer the existing visible label.
    state.originalLabel = state.originalLabel.replace(/^"|"$/g, '');
    addons.forEach(item => { item.input.disabled = item.originalDisabled || item.input.dataset.available !== 'true'; });
    state.change = event => {
      if (event.target.matches('[data-bpl-addon], [name="id"], [name="quantity"], [name="selling_plan"], .single-option-selector, .swatch input[type="radio"]') || String(event.target.name || '').startsWith('properties[')) {
        const addon = state.addons.find(item => item.input === event.target);
        if (addon) { addon.editVersion += 1; editSelection(state); }
        else observeMainChange(state);
        schedule(state);
      }
    };
    state.click = event => {
      const target = event.target.closest ? event.target : event.target.parentElement;
      if (!target) return;
      const addon = state.addons.find(item => target.closest('[data-bpl-addon]') === item.input);
      if (addon) return addonClick(event, state, addon);
      if (state.checkoutButton && target.closest('[data-bpl-checkout]') === state.checkoutButton) return checkoutClick(event, state);
      if (target.closest('.js-change-quantity')) observeMainChange(state);
      if (target.closest('.add_to_cart.ajax-submit') === button) {
        if (checkoutBusy(state) || state.uncertainVersion === state.selectionVersion) {
          event.preventDefault(); event.stopImmediatePropagation(); return;
        }
        armAttempt(state, event);
      }
    };
    state.submit = event => {
      observeMainChange(state, false);
      if (checkoutBusy(state) || state.uncertainVersion === state.selectionVersion) {
        event.preventDefault(); event.stopImmediatePropagation(); return;
      }
      if (!selected(state).length) { armAttempt(state, event); return; }
      event.preventDefault();
      event.stopImmediatePropagation();
      if (!button.disabled && !button.classList.contains('disabled')) button.click();
    };
    form.addEventListener('change', state.change, true);
    form.addEventListener('input', state.change, true);
    form.addEventListener('click', state.click, true);
    form.addEventListener('submit', state.submit, true);
    states.set(panel, state);
    attachCheckout(state);
    if (window.MutationObserver) {
      state.buttonObserver = new window.MutationObserver(() => schedule(state));
      state.buttonObserver.observe(button, { attributes: true, attributeFilter: ['disabled', 'class'], childList: true, subtree: true });
    }
    panel.setAttribute('data-bpl-addons-ready', '');
    bindCartHook();
    sync(state);
    readCart(state, true);
  }

  function init(root) {
    const previousSelector = '.bpl-product--01.bpl-remodel [data-bpl-addons]';
    const previousPanels = [];
    if (root.matches && root.matches(previousSelector)) previousPanels.push(root);
    if (root.querySelectorAll) previousPanels.push(...root.querySelectorAll(previousSelector));
    previousPanels.forEach(panel => {
      if (window.BPLProductAddons && typeof window.BPLProductAddons.unload === 'function') window.BPLProductAddons.unload(panel);
      if (window.BPLTestProductAddons && typeof window.BPLTestProductAddons.unload === 'function') window.BPLTestProductAddons.unload(panel);
      panel.setAttribute('data-bpl-remodel-addons', '');
      panel.removeAttribute('data-bpl-addons');
    });
    if (root.matches && root.matches(selector)) attach(root);
    if (root.querySelectorAll) root.querySelectorAll(selector).forEach(attach);
  }

  function unload(root) {
    states.forEach((state, panel) => {
      if (root !== panel && !root.contains(panel)) return;
      cleanupPayload(state);
      cleanupAddonTransport(state, state.addonTransport);
      state.addons.forEach(item => { if (item.feedbackTimer) window.clearTimeout(item.feedbackTimer); });
      clearAttempt(state);
      if (state.mainEditTimer) window.clearTimeout(state.mainEditTimer);
      state.checkoutIntent = null;
      if (state.checkoutButton) state.checkoutButton.remove();
      if (state.checkoutStatus) state.checkoutStatus.remove();
      if (state.checkoutHost) state.checkoutHost.classList.remove('bpl-checkout-buttons');
      if (state.checkoutLayout) {
        state.checkoutLayout.classList.remove('bpl-checkout-layout');
        state.checkoutLayout.classList.remove('bpl-checkout-with-quantity');
      }
      if (state.frame) window.cancelAnimationFrame(state.frame);
      state.form.removeEventListener('change', state.change, true);
      state.form.removeEventListener('input', state.change, true);
      state.form.removeEventListener('click', state.click, true);
      state.form.removeEventListener('submit', state.submit, true);
      if (state.buttonObserver) state.buttonObserver.disconnect();
      state.addons.forEach(item => { item.input.disabled = item.originalDisabled; });
      if (state.buttonText.textContent === state.lastLabel) state.buttonText.textContent = state.originalLabel;
      panel.hidden = true;
      panel.removeAttribute('data-bpl-addons-ready');
      state.form.classList.remove('bpl-addons-has-selection');
      state.form.classList.remove('bpl-addons-extras-only');
      state.root.classList.remove('bpl-addons-active');
      states.delete(panel);
    });
    if (!states.size && cartHook && window.jQuery) {
      window.jQuery(document).off('.bplRemodelProductAddons');
      cartHook = false;
      refreshAfterRequest = false;
      pendingMutations.clear();
      cartRevision += 1;
      cart = null;
    }
  }

  window.BPLRemodelProductAddons = { init, unload };
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => unload(event.target));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  else init(document);
  window.addEventListener('load', () => init(document), { once: true });
  window.addEventListener('focus', () => { if (states.size) readCart(states.values().next().value); });
  window.addEventListener('pageshow', () => {
    states.forEach(state => { state.navigating = false; state.checkoutIntent = null; schedule(state); });
    if (states.size) readCart(states.values().next().value, true);
  });
})();
